import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { Stack, Typography, Box, TextField, Dialog, DialogContent, Autocomplete, Checkbox } from '@mui/material';
import axios from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';
import updateItemInPlaceOrAppend from '../../utils/updateItemInPlaceOrAppend';

OrderGuideForm.propTypes = {
  editOrderGuide: PropTypes.object,
  setOrderGuides: PropTypes.func,
  purchasingGroups: PropTypes.array,
  productCategories: PropTypes.array,
  facilities: PropTypes.array,
  open: PropTypes.bool,
  close: PropTypes.func
}

export default function OrderGuideForm({ editOrderGuide, setOrderGuides, purchasingGroups, productCategories, facilities, open, close }) {
  const { enqueueSnackbar } = useSnackbar();

  const [submitting, setSubmitting] = useState(false);
  const [formValue, setFormValue] = useState({});

  useEffect(() => {
    if (editOrderGuide) setFormValue({ ...editOrderGuide, usePurchasingGroup: !editOrderGuide.facilityId })
    else setFormValue({});
  }, [open])

  const onSubmit = () => {
    setSubmitting(true);
    axios.post("/orderGuide/orderGuideForm", objectToFormData(formValue))
      .then((res) => {
        if (res.data) {
          enqueueSnackbar(`Order guide ${(editOrderGuide ? "Updated" : "Created")}`, { variant: 'success' });
          res.data.purchasingGroupName = purchasingGroups.find(purchasingGroup => purchasingGroup.value === res.data.purchasingGroupId)?.label;
          res.data.productCategoryName = productCategories.find(productCategory => productCategory.value === res.data.productCategoryId)?.label;
          res.data.facilityName = facilities.find(facility => facility.value === res.data.facilityId)?.label;
          setOrderGuides(orderGuides => updateItemInPlaceOrAppend(orderGuides, res.data));
          close();
        }
        else enqueueSnackbar(`${(editOrderGuide ? "Update" : "Create")} order guide failed`, { variant: 'error' })
      })
      .catch(() => enqueueSnackbar(`${(editOrderGuide ? "Update" : "Create")} order guide failed`, { variant: 'error' }))
      .finally(() => setSubmitting(false));
  };

  return <Dialog open={open} onClose={close} maxWidth="md" >
    <DialogContent>
      <Stack spacing={1} minWidth="600px">
        {formValue.usePurchasingGroup && <Box>
          <Typography variant="subtitle1">Purchasing Group</Typography>
          <Autocomplete
            fullWidth
            size="small"
            value={purchasingGroups.find(purchasingGroup => purchasingGroup.value === formValue.purchasingGroupId) ?? null}
            onChange={(event, val) => setFormValue(form => ({ ...form, purchasingGroupId: val?.value ?? 0 }))}
            options={purchasingGroups}
            renderInput={(params) => <TextField {...params} placeholder="Purchasing Group" />}
          />
        </Box>}
        {!formValue.usePurchasingGroup && <Box>
          <Typography variant="subtitle1">Facility</Typography>
          <Autocomplete
            fullWidth
            size="small"
            value={facilities.find(facility => facility.value === formValue.facilityId) ?? null}
            onChange={(event, val) => setFormValue(form => ({ ...form, facilityId: val?.value ?? 0 }))}
            options={facilities}
            renderInput={(params) => <TextField {...params} placeholder="Facility" />}
          />
        </Box>}
        <Box>
          <Typography variant="subtitle1">Product Category</Typography>
          <Autocomplete
            fullWidth
            size="small"
            value={productCategories.find(productCategory => productCategory.value === formValue.productCategoryId) ?? null}
            onChange={(event, val) => setFormValue(form => ({ ...form, productCategoryId: val?.value ?? 0 }))}
            options={productCategories}
            renderInput={(params) => <TextField {...params} placeholder="Product Category" />}
          />
        </Box>
        <Box>
          <Checkbox checked={formValue.usePurchasingGroup ?? false} onChange={(e) => setFormValue(value => ({ ...value, usePurchasingGroup: e.target.checked }))} />
          Order guide by purchasing group 
        </Box>    
        <Box>
          <LoadingButton loading={submitting} disabled={(formValue.usePurchasingGroup ? !formValue.purchasingGroupId : !formValue.facilityId) || !formValue.productCategoryId}
            sx={{ display: "flex", justifySelf: "center", mb: 2, px: 7 }} color="purple" size="medium" variant="contained" onClick={onSubmit}>
            Save
          </LoadingButton>
        </Box>
      </Stack>
    </DialogContent>
  </Dialog>;
}
